import { ReactNode } from 'react';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ButtonLink from '@v2/shared/components/ButtonLink';
import ContentCard from '@v2/shared/components/ContentCard';
import PageHeading from '@v2/shared/components/PageHeading';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import ReviewShowcase from './ReviewShowcase';

/** A link in body copy, which has no `prose` here to colour it. */
const LINK = 'text-primary-700 underline underline-offset-2';

/**
 * The stat strip's inset from the column's edge, so the hero shows either side of it
 * where it overlaps. Only the strip: the sections below span the full column, as on
 * /refund-policy.
 */
const GUTTER = 'mx-4 md:mx-7';

/** A section of the page: the distance from the one above it. */
const BLOCK = 'mt-9';

/**
 * `rounded-xl` rather than ContentCard's `rounded-lg`: the hero and the stat strip above
 * these are the /reviews ones, which are 12px, and a card with a tighter corner than the
 * panel it sits under reads as two designs stacked.
 */
const CARD = 'rounded-xl';

const SECTION_HEADING = 'm-0 mb-4 text-2xl font-extrabold tracking-tight text-slate-900';

/** Clears the fixed header when a `#` link lands on a section. */
const ANCHORED = 'scroll-mt-14 md:scroll-mt-32 xl:scroll-mt-24';

const BODY = 'mb-2.5 mt-0 text-slate-800';

/** The qualifications under each promise, which close it — hence no bottom margin. */
const FINE = 'mb-0 mt-0 text-sm text-slate-500';

const TH =
  'border-0 border-b border-solid border-slate-200 bg-slate-50 p-3 text-left align-top text-xs font-semibold uppercase tracking-wider text-slate-500';
const TD = 'border-0 border-b border-solid border-slate-200 p-3 text-left align-top';

const AT_A_GLANCE = [
  { figure: '6 months', label: 'to cancel a magazine for a pro-rated refund' },
  { figure: '$0', label: 'shipping on magazine subscriptions' },
  { figure: '10%+', label: 'off list on every DiscountLock renewal' },
  { figure: '30 days', label: 'to return a book' },
  { figure: '24/7', label: 'help, U.S. based team' },
];

const PAYMENT_METHODS = [
  'Visa',
  'Mastercard',
  'American Express',
  'Discover',
  'PayPal',
  'Apple Pay',
];

type PromiseProps = {
  /**
   * Load-bearing: these anchors are linked from elsewhere and may not be renamed.
   * `#satisfaction` and `#books` are the targets of the two badges under the buy buttons
   * on the book product page — see `Pages/Product/Guarantees`.
   */
  id: string;
  /** The figure the promise is measured in — "6", "$0", "24/7". */
  figure: string;
  /** What the figure counts, under it. */
  unit: string;
  heading: string;
  children: ReactNode;
};

/**
 * One promise: the figure it is measured in, and the prose that qualifies it.
 *
 * The figure column collapses above the copy on a phone, so the number reads as a label
 * for what follows rather than as a stray figure beside it.
 */
function PromiseCard({ id, figure, unit, heading, children }: PromiseProps) {
  return (
    <ContentCard
      id={id}
      className={cn(
        CARD,
        ANCHORED,
        'mt-3 grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-[132px_1fr] md:p-6'
      )}
    >
      <div>
        <b className="block whitespace-nowrap text-3xl font-extrabold leading-none tracking-tight text-primary">
          {figure}
        </b>
        <span className="mt-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
          {unit}
        </span>
      </div>

      <div>
        <h2 className="m-0 mb-2 text-lg font-bold leading-snug text-slate-900">{heading}</h2>
        {children}
      </div>
    </ContentCard>
  );
}

/**
 * /guarantee.
 *
 * The copy is the content owner's, not this component's: it may not be re-worded,
 * re-ordered or re-punctuated without a task that supplies the new wording.
 *
 * The design is /reviews': the same gradient hero, the same stat strip pulled up over it,
 * and the shared ContentCard for everything below. That is deliberate and replaced a
 * first pass that set the page as a boxless document — this page is meant to look like
 * the rest of the site, not like its own publication.
 *
 * Every section is deliberately a link to somewhere authoritative rather than a
 * restatement of it: the Refund Policy owns the terms and this page owns only the
 * summary, so a change to either does not silently leave this page contradicting it.
 *
 * The `#` on each promise is part of the contract — they are linked from outside this
 * page, so a rename breaks a link somewhere else in the site.
 *
 * The delivery windows under `#shipping` are deliberately written out rather than taking
 * the `otherpages.estimate` label, which this page used to be handed. That label is a
 * single global row meaning one thing — "the industry standard delivery time for magazine
 * subscriptions is N weeks" — and the live delivery heads-up email says exactly that with
 * it. Spending it on one frequency here made editing it in admin silently rewrite that
 * email for every customer on every brand. A per-frequency schedule cannot come from a
 * one-value label; see `/shipping`, which owns the same
 * schedule and is the page to keep this one agreeing with.
 */
export default function Guarantee() {
  const { buildUrl } = useUrlGenerator();
  const contact = buildUrl('/contact');
  const refundPolicy = buildUrl('/refund-policy');
  const discountLock = buildUrl('/my-account/discountlock');

  return (
    <div className="text-slate-800">
      {/* The page's own trail, not the layout's. */}
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Our Guarantee' }]} />

      <PageHeading
        hero
        eyebrow="Family owned and U.S. based since 2002"
        intro="Most subscription sellers promise satisfaction and leave the details vague. We would rather tell you exactly what you get, how long you have, and where to click."
      >
        Our guarantee, in writing.
      </PageHeading>

      {/* Pulled up over the bottom of the hero, which is padded out to leave room for it. */}
      <ul
        aria-label="Guarantee at a glance"
        className={cn(
          GUTTER,
          'relative -mt-16 grid list-none grid-cols-2 rounded-xl border border-solid border-slate-200 bg-white p-0 md:grid-cols-5'
        )}
      >
        {AT_A_GLANCE.map((item, index) => (
          <li
            key={item.figure}
            className={cn(
              'px-2 py-3.5 text-center text-xs text-slate-500',
              'border-0 border-solid border-slate-200 md:border-t-0',
              // Dividers between cells: two columns on a phone, five above it.
              index % 2 === 1 && 'border-l',
              index >= 2 && 'border-t',
              index > 0 && 'md:border-l',
              // Five of them in two columns leaves the last one alone on its row, so it
              // takes the whole of it rather than half.
              index === AT_A_GLANCE.length - 1 && 'col-span-2 md:col-auto'
            )}
          >
            <b className="block text-xl font-extrabold leading-tight text-primary">{item.figure}</b>
            {item.label}
          </li>
        ))}
      </ul>

      <section className={BLOCK}>
        <h2 className={SECTION_HEADING}>What we promise</h2>

        <PromiseCard
          id="satisfaction"
          figure="6"
          unit="months"
          heading="Not happy with a magazine? Cancel and get the rest back."
        >
          <p className={BODY}>
            If you are not satisfied with a print or digital magazine subscription for any reason
            within 6 months of purchase,{' '}
            <a className={LINK} href={contact}>
              contact us
            </a>{' '}
            to cancel it. We refund every issue not yet delivered, pro-rated, to your original
            payment method. The reason does not matter. No restocking fee, no store credit, no
            argument.
          </p>
          <p className={BODY}>
            Pro-rated means simple math: pay $24 for 12 issues, receive 4, and we refund the other 8
            at $2.00 each, so $16.00. We process refunds within 48 hours and most banks post them
            within 5 to 7 business days.
          </p>
          <p className={FINE}>
            Single digital issues are delivered to your Zinio or Magzter account within minutes and
            can only be cancelled from your dashboard before delivery. Orders that received cash
            back, a rebate, a reward, or virtual currency can be cancelled but are not eligible for
            a refund. Full terms in our{' '}
            <a className={LINK} href={refundPolicy}>
              Refund Policy
            </a>
            .
          </p>
        </PromiseCard>

        <PromiseCard
          id="pricematch"
          figure="30"
          unit="days"
          heading="Find it cheaper? We match it."
        >
          <p className={BODY}>
            If you find the same magazine subscription (same title, term, and format) advertised for
            less by an authorized seller, or on our own site, send us the link and we will match the
            price. This works before you order and for up to 30 days after your order date.
          </p>
          <p className={FINE}>
            We need a working link to the offer so we can verify it. Coupons, gift certificates, and
            prices reached through cash-back or rewards sites cannot be combined with a price match.{' '}
            <a className={LINK} href={contact}>
              Request a price match
            </a>
            .
          </p>
        </PromiseCard>

        <PromiseCard
          id="shipping"
          figure="$0"
          unit="shipping"
          heading="The price you see is the price you pay."
        >
          <p className={BODY}>
            Magazine subscriptions ship free to your mailbox. There are no handling fees, no
            processing fees, and no surprise charges at checkout. Digital subscriptions and single
            issues reach your Zinio or Magzter account within 60 minutes.
          </p>
          <p className={FINE}>
            Publishers pre-print mailing labels weeks ahead, so the first issue of a new print
            subscription takes time: about 4 to 6 weeks for weeklies, 6 to 9 weeks for monthlies, 8
            to 12 weeks for bi-monthly and seasonal titles, and 10 to 12 weeks for quarterlies.
            Later issues arrive on the publisher&apos;s normal schedule. Waiting more than 12 weeks?{' '}
            <a className={LINK} href={contact}>
              Tell us
            </a>{' '}
            and we open an investigation with the publisher. Books ship separately with tracking;
            standard shipping is $4.99 and Media Mail arrives in 4 to 10 days.
          </p>
        </PromiseCard>

        <PromiseCard
          id="discountlock"
          figure="10%"
          unit="minimum"
          heading="Renewals are announced in advance, always discounted, and yours to switch off."
        >
          <p className={BODY}>
            Magazine subscriptions renew automatically through DiscountLock so you never miss an
            issue between terms. Every renewal is guaranteed at least 10% off the publisher&apos;s
            list price at the time it renews. If we cannot give you that discount, we do not renew;
            we email you instead. If a magazine is discontinued, we substitute a similar title of
            equal or greater value at the same or better price and tell you first, so you can cancel
            before the renewal is processed if you would rather not have it.
          </p>
          <p className={BODY}>
            You always know before you are charged: one email 30 days out, and another 7 days out
            with the exact amount. Turn DiscountLock off any time with one click at{' '}
            <a className={LINK} href={discountLock}>
              My Account, DiscountLock
            </a>
            , through the{' '}
            <a className={LINK} href={contact}>
              contact form
            </a>
            , or by phone. Cancel before the process date and nothing is charged. A renewal that has
            already gone through is a new order with its own 6-month refund window.
          </p>
          <p className={FINE}>
            DiscountLock cannot be switched back on once you disable it; a new order is required.
            In some states, including Vermont, DiscountLock is enrolled only if you opt in at
            checkout. Zinio Unlimited and Magzter Gold memberships can also be cancelled any time;
            access continues to the end of the paid period. Magzter Gold carries a 7-day
            no-questions refund, Zinio Unlimited does not refund partial months. Details in our{' '}
            <a className={LINK} href={refundPolicy}>
              Refund Policy
            </a>
            .
          </p>
        </PromiseCard>

        <PromiseCard id="books" figure="30" unit="days" heading="Books can come back.">
          <p className={BODY}>
            Received a book that is damaged, incorrect, or incomplete?{' '}
            <a className={LINK} href={contact}>
              Contact us first
            </a>{' '}
            and we fix it. Changed your mind? Return any book or other physical item within 30 days
            of delivery in its original condition for a refund of the purchase price plus tax.
          </p>
          <p className={FINE}>
            Shrink-wrapped items must be unopened. Return shipping is your responsibility and must
            include tracking. Original shipping fees are not refunded. Details in our{' '}
            <a className={LINK} href={refundPolicy}>
              Refund Policy
            </a>
            .
          </p>
        </PromiseCard>

        <PromiseCard
          id="support"
          figure="24/7"
          unit="support"
          heading="Help any time, from a small team that reads every ticket."
        >
          <p className={BODY}>
            Write to us through the{' '}
            <a className={LINK} href={contact}>
              contact form
            </a>{' '}
            whenever you like. Simple things like order status, an address change, or a renewal
            question get answered right away, around the clock. Anything that needs judgment, like a
            missing issue or a refund, goes to our U.S. based customer care team, and they reply on
            business days. Once you are a customer, you also get exclusive phone support; the number
            is in your order confirmation email and on your card statement.
          </p>
          <p className={FINE}>
            One request: if something is wrong with a charge, come to us before your bank. We can
            refund within 24 to 48 hours. A bank dispute freezes the order for up to 80 days and
            stops us from helping.
          </p>
        </PromiseCard>
      </section>

      <section className={BLOCK}>
        <h2 className={SECTION_HEADING}>Everything above, in one table</h2>

        {/* The table scrolls rather than squeezing on a narrow screen, and the wrapper is
            what scrolls: `display:block` on the table itself is the usual shortcut and
            costs the element its table semantics in some screen readers. It takes a
            tabindex because a region that scrolls has to scroll without a mouse. */}
        <ContentCard className={cn(CARD, 'overflow-hidden p-0 md:p-0')}>
          <div className="w-full overflow-x-auto" tabIndex={0}>
            <table className="w-full min-w-[520px] border-collapse text-sm [&_tr:last-child_td]:border-b-0">
              <thead>
                <tr>
                  <th className={TH}>What we promise</th>
                  <th className={TH}>How long you have</th>
                  <th className={TH}>Where to go</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={TD}>Cancel a magazine subscription for a pro-rated refund</td>
                  <td className={TD}>6 months from purchase</td>
                  <td className={TD}>
                    <a className={LINK} href={contact}>
                      Contact us
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className={TD}>Price match an authorized lower offer</td>
                  <td className={TD}>30 days from order date</td>
                  <td className={TD}>
                    <a className={LINK} href={contact}>
                      Contact us
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className={TD}>Cancel a DiscountLock renewal for a full refund</td>
                  <td className={TD}>Any time before the process date</td>
                  <td className={TD}>
                    <a className={LINK} href={discountLock}>
                      My Account
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className={TD}>Turn off DiscountLock, Zinio Unlimited, or Magzter Gold</td>
                  <td className={TD}>Any time</td>
                  <td className={TD}>
                    <a className={LINK} href={discountLock}>
                      My Account
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className={TD}>Return a book in original condition</td>
                  <td className={TD}>30 days from delivery</td>
                  <td className={TD}>
                    <a className={LINK} href={contact}>
                      Contact us
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className={TD}>Report a first issue that has not arrived</td>
                  <td className={TD}>After 12 weeks</td>
                  <td className={TD}>
                    <a className={LINK} href={contact}>
                      Contact us
                    </a>
                  </td>
                </tr>
                <tr>
                  <td className={TD}>Free shipping on magazine subscriptions</td>
                  <td className={TD}>Always</td>
                  <td className={TD}>Applied at checkout</td>
                </tr>
                <tr>
                  <td className={TD}>Get help</td>
                  <td className={TD}>Any time</td>
                  <td className={TD}>
                    <a className={LINK} href={contact}>
                      Contact us
                    </a>
                    , or the phone number in your order confirmation
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ContentCard>
      </section>

      <section id="reviews" className={cn(BLOCK, ANCHORED)}>
        <h2 className={SECTION_HEADING}>Verified customer reviews</h2>
        <ReviewShowcase />
      </section>

      <section className={BLOCK}>
        <h2 className={SECTION_HEADING}>Checkout is encrypted and your card stays yours</h2>

        <ContentCard className={CARD}>
          <p className="m-0 text-slate-800">
            Every page on DiscountMags.com is served over HTTPS, so your personal and payment
            details are encrypted between your browser and us. Orders are placed on the website
            only; we never take card numbers over the phone. Pay the way you prefer:
          </p>

          <ul className="m-0 mt-3 flex list-none flex-wrap gap-2 p-0">
            {PAYMENT_METHODS.map((method) => (
              <li
                key={method}
                className="rounded-full border border-solid border-slate-200 bg-white px-3 py-1 text-sm font-semibold text-slate-600"
              >
                {method}
              </li>
            ))}
          </ul>
        </ContentCard>
      </section>

      <ContentCard className={cn(CARD, BLOCK, 'text-center')}>
        <h2 className="m-0 mb-1.5 text-2xl font-extrabold tracking-tight text-slate-900">
          Still deciding?
        </h2>
        <p className="mx-auto mb-5 mt-0 max-w-[34rem] text-slate-600">
          Ask us anything before you order. We would rather answer a question now than process a
          refund later.
        </p>

        <div className="flex flex-wrap justify-center gap-2.5">
          <ButtonLink href={contact} className="border border-solid border-primary">
            Contact customer service
          </ButtonLink>
          {/* The ghost of the same button, so the pair share a box. */}
          <ButtonLink
            href={buildUrl('/help')}
            className="border border-solid border-primary bg-white text-primary hover:bg-primary hover:text-white"
          >
            Browse the Help Center
          </ButtonLink>
        </div>

        <p className="mx-auto mb-0 mt-6 max-w-[34rem] text-sm text-slate-500">
          When you order from us, you are supporting a small, independent business that cares about
          reading as much as you do. Thank you. <b>The DiscountMags team</b>
        </p>
      </ContentCard>
    </div>
  );
}

Guarantee.layoutName = 'Layouts.Institutional';
